import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { SocketClient } from './socket';

/**
 * One socket for the whole logged-in app.
 *
 * `SocketClient` takes a single `onResync` callback, but more than one feature
 * needs to refetch after a reconnect — Kulčina's display today, the showings
 * list and the seat map later. So the provider owns the callback and fans it
 * out to whoever registered, rather than every feature opening its own socket.
 */

interface SocketApi {
  socket: SocketClient;
  /** Called after the socket comes back up. Returns an unsubscribe. */
  onResync: (fn: () => void) => () => void;
}

const SocketContext = createContext<SocketApi | null>(null);

export function SocketProvider({ children }: { children: ReactNode }) {
  const resyncers = useRef(new Set<() => void>());

  // Built once and kept for the life of the provider. The provider is mounted
  // only behind the door, so a socket existing at all implies a session.
  const socket = useMemo(
    () =>
      new SocketClient(() => {
        for (const fn of resyncers.current) fn();
      }),
    [],
  );

  const onResync = useCallback((fn: () => void) => {
    resyncers.current.add(fn);
    return () => {
      resyncers.current.delete(fn);
    };
  }, []);

  useEffect(() => {
    socket.connect();
    return () => socket.disconnect();
  }, [socket]);

  const value = useMemo<SocketApi>(() => ({ socket, onResync }), [socket, onResync]);
  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket(): SocketApi {
  const value = useContext(SocketContext);
  if (!value) throw new Error('useSocket outside SocketProvider');
  return value;
}
